import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ContentContainer from '../../areas/layout/content-container'
import { PageWrapper } from '../Profile/profile-styles'
import { useAuth } from '../../context/AuthContext'
import { useSettings, SectionKey } from './use-settings'
import { CenterBox } from '../../utils/center-box'
import { NotLoggedIn } from '../../utils/not-logged-in'
import { useMobileBreakpoints } from '../../utils/mobile-breakpoints'
import { SettingsNav, SECTIONS } from './settings-nav'
import { ProfileSection } from './sections/profile-section'
import { PreferencesSection } from './sections/preferences-section'
import { NotificationsSection } from './sections/notifications-section'
import { PrivacySection } from './sections/privacy-section'
import { DataSection } from './sections/data-section'

const SettingsLayout = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '224px minmax(0, 1fr)',
  gap: theme.spacing(4),
  alignItems: 'start',
  marginTop: theme.spacing(3),
}))

// The active section sits in its own card so the page reads as one panel
// rather than the old stack of five.
const ContentCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}))

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: 28,
  fontWeight: 700,
  color: theme.palette.text.primary,
}))

const EmailOriginBadge = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(0.5, 1.25),
  marginBottom: theme.spacing(2),
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.brand.main,
  border: `1px solid ${theme.palette.brand.main}`,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(139, 30, 63, 0.22)'
      : 'rgba(139, 30, 63, 0.07)',
}))

const MobileAccordion = styled(Accordion)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  borderRadius: `${Number(theme.shape.borderRadius) * 2}px !important`,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  '&::before': { display: 'none' },
}))

const AccordionLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  fontSize: 15,
  fontWeight: 600,
  '& .MuiSvgIcon-root': {
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
}))

const FixedCenterOverlay = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1300,
})

const SettingsPage = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const { isMobileMD } = useMobileBreakpoints()
  const {
    settings,
    loading,
    saving,
    refreshing,
    isDisabled,
    activeSection,
    selectSection,
    showEmailOrigin,
    handleNotificationChange,
    handleAllNotificationsToggle,
    handleTopLevelToggle,
    handleThemeChange,
    themePreference,
    updateRemote,
    setSettings,
  } = useSettings()

  if (isLoading) {
    return (
      <ContentContainer>
        <CenterBox>
          <CircularProgress size={48} />
        </CenterBox>
      </ContentContainer>
    )
  }

  if (!isAuthenticated) {
    return (
      <ContentContainer>
        <NotLoggedIn message='Please log in to access your settings.' />
      </ContentContainer>
    )
  }

  const renderSection = (section: SectionKey) => {
    switch (section) {
      case 'profile':
        return <ProfileSection />
      case 'preferences':
        return (
          <PreferencesSection
            settings={settings}
            themePreference={themePreference}
            isDisabled={isDisabled}
            onThemeChange={handleThemeChange}
            onUpdate={updateRemote}
            setSettings={setSettings}
          />
        )
      case 'notifications':
        return (
          <NotificationsSection
            settings={settings}
            isDisabled={isDisabled}
            onChange={handleNotificationChange}
            onToggleAll={handleAllNotificationsToggle}
            onTopLevelToggle={handleTopLevelToggle}
          />
        )
      case 'privacy':
        return (
          <PrivacySection
            settings={settings}
            isDisabled={isDisabled}
            onUpdate={updateRemote}
            setSettings={setSettings}
          />
        )
      case 'data':
        return <DataSection />
    }
  }

  return (
    <ContentContainer>
      <PageWrapper>
        <PageTitle>Settings</PageTitle>

        {isMobileMD ? (
          <Box sx={{ mt: 2 }}>
            {SECTIONS.map((section) => (
              <MobileAccordion
                key={section.key}
                expanded={activeSection === section.key}
                onChange={() => selectSection(section.key)}
                disableGutters
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <AccordionLabel>
                    {section.icon}
                    {section.label}
                  </AccordionLabel>
                </AccordionSummary>
                <AccordionDetails>
                  {section.key === 'notifications' && showEmailOrigin && (
                    <EmailOriginBadge>Opened from your email</EmailOriginBadge>
                  )}
                  {renderSection(section.key)}
                </AccordionDetails>
              </MobileAccordion>
            ))}
          </Box>
        ) : (
          <SettingsLayout>
            <SettingsNav active={activeSection} onSelect={selectSection} />
            <ContentCard>
              {activeSection === 'notifications' && showEmailOrigin && (
                <EmailOriginBadge>Opened from your email</EmailOriginBadge>
              )}
              {renderSection(activeSection)}
            </ContentCard>
          </SettingsLayout>
        )}
      </PageWrapper>

      {(loading || saving || refreshing) && (
        <FixedCenterOverlay>
          <CircularProgress size={48} />
        </FixedCenterOverlay>
      )}
    </ContentContainer>
  )
}

export default SettingsPage
